import { MutationCache, QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

// Offline writes ("outbox" mutations)
// -----------------------------------
// Every queueable write carries a stable `['outbox', ...]` mutationKey and is
// registered with `queryClient.setMutationDefaults(key, { mutationFn })` in
// its feature hooks file. Paused mutations are persisted (lib/persist.ts) and
// survive reload — but their component closures do not; on restore, the drain
// (lib/offline.ts) re-runs them through the function registered for their
// key. lib/outbox.ts eagerly imports every registering module so replay can
// never race a lazily-loaded route chunk. Rules for new mutations live in
// apps/frontend/AGENTS.md.

// Stamps a client-generated UUID onto a create body. The id must ride in the
// mutation *variables* (not inside the mutationFn) so an offline replay
// re-sends the exact same id — making the create idempotent on the backend
// and the record addressable while still offline.
export function withClientId<T extends object>(body: T): T & { id: string } {
  return {
    ...body,
    id: 'id' in body && typeof body.id === 'string' ? body.id : crypto.randomUUID(),
  }
}

// Every failed mutation surfaces a toast unless the mutation opts out with
// `meta: { silent: true }` (e.g. flows that already render their own inline
// error). Query failures stay inline — most lists/cards already render an
// error state, and a toast per failed query would spam when the backend is
// down. Offline-paused mutations never error here: they queue (networkMode
// 'online') and land in the pending-changes drawer instead.
const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    if (mutation.meta?.silent) return
    toast.error(error.message || 'Something went wrong')
  },
})

export const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      // Data is considered fresh for 1 minute.
      // Components mounting/unmounting won't trigger refetches during this time.
      staleTime: 1000 * 60,

      // Unused queries are retained for a week so the persisted cache
      // (lib/persist.ts) has offline-worthy data to save — gcTime is what
      // evicts entries; persistence just serializes whatever remains.
      gcTime: 1000 * 60 * 60 * 24 * 7,

      // Retry failed requests once before showing an error state.
      retry: 1,

      // Turn off automatic refetching on window focus.
      // Users often switch tabs; we don't want to hammer the backend accidentally.
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Default networkMode 'online': a mutation submitted while offline
      // PAUSES (queued, persisted via lib/persist.ts) and replays when
      // connectivity returns — that pause is what fills the offline queue.
      // ('offlineFirst' would attempt-and-fail: mutations don't retry, so the
      // write would hard-error instead of queueing.) Mutations that must
      // never queue — session lifecycle and FormData uploads — pin
      // networkMode 'always' to fail fast offline.
    },
  },
})
